<template>
  <modal
    elem-id="modal_pdv_cancelar_os"
    :show="isVisible"
    :title="t('Cancelar OS')"
    :close-label="t('Voltar')"
    :save-label="t('Confirmar cancelamento')"
    :save-fn="handleConfirm"
    :save-disabled-fn="() => !isReasonValid || isCancelling"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <box type="warning" :title="t('Motivo obrigatório')">
        <p>{{ t('O cancelamento é definitivo. Informe o motivo — ele fica registrado na OS.') }}</p>

        <input-text-area
          elem-id="pdv_cancelar_os_motivo"
          v-model="reason"
          :rows="4"
          :required="true"
          :label="t('Motivo do cancelamento')"
          :placeholder="t('Descreva o motivo do cancelamento')"
          data-testid="pdv-cancelar-os-motivo-input">
        </input-text-area>
      </box>

      <div v-if="isCancelling" class="text-center" style="margin-top: 15px;">
        <ProgressSpinner style="width: 1rem; height: 1rem" strokeWidth="4" /> {{ t('Cancelando OS...') }}
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

/** Mirrors the backend Orders_CancelInput rule (>= 5 chars after trim). */
const MIN_REASON_LENGTH = 5;

const emit = defineEmits<{
  close: [];
  confirm: [reason: string];
}>();

const { t } = useI18n();

const isVisible = ref(false);
const reason = ref('');
const isCancelling = ref(false);

const isReasonValid = computed<boolean>(() => reason.value.trim().length >= MIN_REASON_LENGTH);

const open = (): void => {
  log('PDVCancelarOSModal', 'open', 'Opening cancel-OS modal');
  reason.value = '';
  isCancelling.value = false;
  isVisible.value = true;
};

const close = (): void => {
  log('PDVCancelarOSModal', 'close', 'Closing cancel-OS modal');
  isVisible.value = false;
  reason.value = '';
  isCancelling.value = false;
  emit('close');
};

const handleClose = (): void => {
  close();
};

const handleConfirm = async (): Promise<void> => {
  if (!isReasonValid.value) {
    eventBus.emit('globalMessage.error', t('Informe o motivo do cancelamento'));
    return;
  }

  isCancelling.value = true;
  const trimmed = reason.value.trim();
  log('PDVCancelarOSModal', 'handleConfirm', 'Confirming cancellation');

  // The caller owns the API call and its error reporting; this modal only
  // collects and validates the reason.
  emit('confirm', trimmed);
  eventBus.emit('pdv.cancelar-os.confirmed', { reason: trimmed });
  close();
};

onMounted(() => {
  eventBus.on('modal.pdv-cancelar-os.open', open);
  eventBus.on('modal.pdv-cancelar-os.close', close);
});

onBeforeUnmount(() => {
  eventBus.off('modal.pdv-cancelar-os.open', open);
  eventBus.off('modal.pdv-cancelar-os.close', close);
});

defineExpose({
  open,
  close
});
</script>
